(ns app.front.view.admin.tenant-management.overview-tab
  "Overview tab of the tenant detail view: base details and products, with an
  editor for the details. Country and province/state follow from the
  municipalities, so they are not editable here. `:admin/tenant-edit-details`
  reads the rest of the tenant server-side, so an edit cannot drop scopes or
  products."
  (:require [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.key-value :as ui-kv]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.view.admin.domain-groups.municipality-groups :as mg]
            [app.front.view.admin.tenant-management.common :as tm-common]
            [app.front.view.admin.tenant-management.external-module-subs :as em-subs]
            [clojure.string :as str]
            [core.domain.validators :as v]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; ---------------------------------------------------------------------------
;; Edit mode state and saving
;; ---------------------------------------------------------------------------

(defn- ->draft [group]
  {:group-name   (:group/name group)
   ;; The command wants the bare number, not the prefixed group code.
   :group-number (mg/group-id->group-number (:group/id group))
   :group-domain (:group/domain group)
   :timezone     (:group/tz group)
   :currency     (:group/currency group)})

;; The edit of one tenant's details: `:tenant-id`, `:draft`, and `:unlocked?`
;; once the group code has been unlocked for editing.
(rf/reg-event-db ::edit
  (fn [db [_ group]]
    (assoc db ::edit {:tenant-id (:db/id group)
                      :draft     (->draft group)})))

(rf/reg-event-db ::cancel-edit
  (fn [db _]
    (dissoc db ::edit)))

(rf/reg-event-db ::set-draft-value
  (fn [db [_ k v]]
    (assoc-in db [::edit :draft k] v)))

(rf/reg-event-db ::unlock-group-number
  (fn [db _]
    (assoc-in db [::edit :unlocked?] true)))

(rf/reg-sub ::edit
  (fn [db [_ tenant-id]]
    (let [edit (::edit db)]
      (when (= tenant-id (:tenant-id edit))
        edit))))

(rf/reg-event-fx ::save
  (fn [_ [_ tenant-id draft]]
    {:comm/command {:command    :admin/tenant-edit-details
                    :data       (assoc draft :domain-group-id tenant-id)
                    :notify     {:success :changes-saved}
                    :on-success [::save.success]}}))

(rf/reg-event-fx ::save.success
  (fn [_ [_ & command-result]]
    ;; The new name cascades to the authority groups, so let the generic
    ;; entity-change handling take care of all of them. A rejected save leaves
    ;; the draft to be corrected.
    {:fx [[:dispatch (into [::entities/apply-changes] command-result)]
          [:dispatch [::cancel-edit]]]}))

;; ---------------------------------------------------------------------------
;; Details card
;; ---------------------------------------------------------------------------

(defn- trimmed
  "The details as sent, trimmed of surrounding whitespace."
  [draft]
  (p/map-vals #(cond-> % (string? %) str/trim) draft))

(defn- valid? [draft]
  (let [{:keys [group-name group-number group-domain timezone currency]} (trimmed draft)]
    (and (not (str/blank? group-name))
         (workspace-model/group-number? group-number)
         (v/valid-domain? group-domain)
         (some? timezone)
         (some? currency))))

(defn- region-values
  "Country and province/state come from the municipalities in the tenant's
  scopes, so they are shown but not editable here."
  [tr {:keys [country] :as tenant}]
  [{:label (tr [:admin/country-code])
    :value (when country (tr [(keyword "country" (name country))]))}
   {:label (tr [:admin/subdivision])
    :value [tm-common/subdivision-cell tr tenant]}])

(defn- set-draft-value [k]
  #(>evt [::set-draft-value k %]))

(defn- set-draft-text [k]
  #(>evt [::set-draft-value k (-> % .-target .-value)]))

(defn- group-number-input
  "Changing the group code renames every group under the tenant and changes
  application ids, so it takes a deliberate second click, like the existing
  municipality-groups dialog."
  [tr {:keys [draft unlocked?]}]
  [:div
   [ui/text-input {:id          "tenant-group-number"
                   :label-text  (tr [:admin/group-id])
                   :placeholder (tr [:placeholder/municipality-code])
                   :pattern     workspace-model/group-number-pattern
                   :disabled    (not unlocked?)
                   :value       (:group-number draft)
                   :invalid     (not (workspace-model/group-number? (str/trim (:group-number draft))))
                   ;; Trimmed on save, not per keystroke, so that typing is
                   ;; not fought with.
                   :on-change   (set-draft-text :group-number)}]
   (if unlocked?
     [:p.text-sm.font-bold.text-ui-red.mt-2 (tr [:admin/edit-group-number-confirmation])]
     [ui/button {:kind       :danger
                 :size       :small
                 :icon       i/warning
                 :class-name "mt-2"
                 :on-click   #(>evt [::unlock-group-number])}
      (tr [:edit])])])

(defn- details-form [tr {:keys [group] :as tenant} {:keys [draft] :as edit}]
  (let [tenant-id  (:db/id group)
        timezones  (<sub [::mg/timezones])
        currencies (<sub [::mg/currencies])
        saving?    (<sub [:comm/command-processing? :admin/tenant-edit-details])]
    [:div.flex.flex-col.gap-4
     [ui/text-input {:id         "tenant-group-name"
                     :label-text (tr [:admin/group-name])
                     :required   true
                     :value      (:group-name draft)
                     :on-change  (set-draft-text :group-name)}]
     [group-number-input tr edit]
     [ui/text-input {:id           "tenant-group-domain"
                     :label-text   (tr [:admin/group-domain-label])
                     :placeholder  (tr [:admin/placeholder-domain])
                     :required     true
                     :value        (:group-domain draft)
                     :invalid      (not (v/valid-domain? (str/trim (:group-domain draft))))
                     :invalid-text (tr [:admin/invalid-domain])
                     :on-change    (set-draft-text :group-domain)}]
     [ui-select/select {:id           "tenant-currency"
                        :label-text   (tr [:admin/currency])
                        :required     true
                        :values       currencies
                        :value        (:currency draft)
                        :val->str     str
                        :allow-empty? false
                        :on-change    (set-draft-value :currency)}]
     [ui-select/select {:id           "tenant-timezone"
                        :label-text   (tr [:admin/timezone])
                        :required     true
                        :values       timezones
                        :value        (:timezone draft)
                        :val->str     str
                        :allow-empty? false
                        :on-change    (set-draft-value :timezone)}]
     [:div
      [:p.text-sm.text-gray-600.mb-3 (tr [:admin/tenant-region-derived])]
      [ui-kv/key-values {:values (region-values tr tenant)}]]
     [:div.flex.justify-end.gap-2
      [ui/button {:kind     :secondary
                  :size     :small
                  :on-click #(>evt [::cancel-edit])}
       (tr [:cancel])]
      [ui/button {:kind     :primary
                  :size     :small
                  :disabled (or saving?
                                (= draft (->draft group))
                                (not (valid? draft)))
                  :on-click #(>evt [::save tenant-id (trimmed draft)])}
       (tr [:save])]]]))

(defn- deactivate-button
  "Deactivating drops the tenant's scopes, which is what makes it inactive.
  Reuses the existing `:admin/disable-domain-group` command."
  [tr {:keys [group active?]}]
  (if active?
    [ui/modal-confirmation-button
     {:tr                  tr
      :modal-title         (tr [:admin/deactivate-group])
      ;; The card it sits in already says which tenant this is.
      :trigger-button-text (tr [:deactivate])
      :trigger-button-opts {:size :small :kind :danger :icon i/remove}
      :disabled?           (<sub [:comm/command-processing? :admin/disable-domain-group])
      :on-yes-click        #(>evt [::mg/disable-domain-group (:db/id group)])}
     (tr [:admin/deactivate-group-confirmation]
         [(:group/name group) (:group/id group)])]
    [:span.text-sm.font-bold.uppercase.text-red-600 (tr [:committee.state/inactive])]))

(defn- details-read-view [tr {:keys [group] :as tenant}]
  [ui-kv/key-values {:values (concat [{:label (tr [:admin/group-name])
                                       :value (:group/name group)}
                                      {:label (tr [:admin/group-id])
                                       :value (:group/id group)}
                                      {:label (tr [:admin/group-domain-label])
                                       :value (:group/domain group)}]
                                     (region-values tr tenant)
                                     [{:label (tr [:admin/currency])
                                       :value (:group/currency group)}
                                      {:label (tr [:admin/timezone])
                                       :value (:group/tz group)}])}])

;; ---------------------------------------------------------------------------
;; Tab
;; ---------------------------------------------------------------------------

(defn overview-tab [tr {:keys [group permit-types] :as tenant}]
  (let [edit (<sub [::edit (:db/id group)])]
    ;; `mt-6` keeps the first card the same distance below the tab title as the
    ;; other tabs, whose plain <section> gets that margin from the global styles.
    [:div.grid.gap-4.mt-6 {:class "lg:grid-cols-[2fr_1fr]"}
     [section/section
      :tenant-overview-details
      {:tr               tr
       :always-expanded? true
       :class            "!my-0"
       :heading-button   [:div.flex.items-center.gap-2
                          [deactivate-button tr tenant]
                          ;; A deactivated tenant is read-only here.
                          (when (and (:active? tenant) (not edit))
                            [ui/button {:kind     :secondary
                                        :size     :small
                                        :icon     i/pen
                                        :on-click #(>evt [::edit group])}
                             (tr [:edit])])]}
      :admin/tenant-details
      (if edit
        [details-form tr tenant edit]
        [details-read-view tr tenant])]
     [section/section
      :tenant-overview-products
      {:tr tr :always-expanded? true :class "!my-0"}
      :admin/products
      (let [external-modules (tm-common/tenant-external-modules (<sub [::em-subs/catalogue-by-system]) tenant)]
        (if (or (seq permit-types) (seq external-modules))
          ;; Stacked in a column once the card is in its own narrow grid track
          ;; (lg and up); below that it wraps across the full width.
          [tm-common/product-tags tr permit-types
           {:color            :light-gray
            :class            "lg:flex-col lg:flex-nowrap lg:items-start"
            :external-modules external-modules}]
          [:p.text-gray-500 (tr [:none])]))]]))
